import type { Client } from '../../models/Client';
import { AuditActionType, ClientStatus } from '../../models/enums';
import { apiClient } from '../api/apiClient';
import { auditLogService } from './auditLogService';
import { createId, delay, readCollection, seedCollection, writeCollection } from './dataverseClient';
import { clientsSeed } from './mockData';

const ENTITY = 'cr_crm_client';

class ClientService {
  private ensureSeed(): void {
    seedCollection(ENTITY, clientsSeed);
  }

  async list(search = '', status?: ClientStatus): Promise<Client[]> {
    // Use real backend when VITE_API_BASE_URL is configured
    if (apiClient.isConfigured()) {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (status) params.set('status', String(status));
      const qs = params.toString();
      const res = await apiClient.get<{ totalCount: number; items: Client[] } | Client[]>(`/api/clients${qs ? `?${qs}` : ''}`);
      return Array.isArray(res) ? res : (res as { items: Client[] }).items ?? [];
    }

    this.ensureSeed();
    await delay();
    const term = search.trim().toLowerCase();
    return readCollection<Client>(ENTITY)
      .filter((item) => (status ? item.cr_crm_status === status : true))
      .filter((item) => !term || [item.cr_crm_companyname, item.cr_crm_domain, item.cr_crm_email].filter(Boolean).some((value) => value?.toLowerCase().includes(term)))
      .sort((a, b) => a.cr_crm_companyname.localeCompare(b.cr_crm_companyname));
  }

  async getById(id: string): Promise<Client | null> {
    if (apiClient.isConfigured()) {
      return apiClient.get<Client>(`/api/clients/${id}`);
    }
    this.ensureSeed();
    await delay();
    return readCollection<Client>(ENTITY).find((item) => item.cr_crm_clientid === id) ?? null;
  }

  async create(payload: Omit<Client, 'cr_crm_clientid'>): Promise<Client> {
    if (apiClient.isConfigured()) {
      return apiClient.post<Client>('/api/clients', payload);
    }
    this.ensureSeed();
    await delay();
    const next: Client = { ...payload, cr_crm_clientid: createId('client'), createdon: new Date().toISOString(), modifiedon: new Date().toISOString() };
    writeCollection(ENTITY, [...readCollection<Client>(ENTITY), next]);
    await auditLogService.record({ actionType: AuditActionType.Create, entityName: 'Client', entityId: next.cr_crm_clientid, description: `Client ${next.cr_crm_companyname} created.` });
    return next;
  }

  async update(id: string, payload: Partial<Client>): Promise<Client> {
    if (apiClient.isConfigured()) {
      return apiClient.put<Client>(`/api/clients/${id}`, payload);
    }
    this.ensureSeed();
    await delay();
    const items = readCollection<Client>(ENTITY);
    const current = items.find((item) => item.cr_crm_clientid === id);
    if (!current) {
      throw new Error('Client not found.');
    }

    const next: Client = { ...current, ...payload, cr_crm_clientid: id, modifiedon: new Date().toISOString() };
    writeCollection(ENTITY, items.map((item) => (item.cr_crm_clientid === id ? next : item)));
    await auditLogService.record({ actionType: AuditActionType.Update, entityName: 'Client', entityId: id, description: `Client ${next.cr_crm_companyname} updated.` });
    return next;
  }

  async delete(id: string): Promise<void> {
    this.ensureSeed();
    await delay();
    const items = readCollection<Client>(ENTITY);
    const current = items.find((item) => item.cr_crm_clientid === id);
    writeCollection(ENTITY, items.filter((item) => item.cr_crm_clientid !== id));
    if (current) {
      await auditLogService.record({ actionType: AuditActionType.Delete, entityName: 'Client', entityId: id, description: `Client ${current.cr_crm_companyname} deleted.` });
    }
  }

  async sync(id: string): Promise<Client> {
    const updated = await this.update(id, { cr_crm_lastsyncat: new Date().toISOString(), cr_crm_syncerror: undefined });
    await auditLogService.record({ actionType: AuditActionType.Sync, entityName: 'Client', entityId: id, description: `Partner Center synchronization completed for ${updated.cr_crm_companyname}.` });
    return updated;
  }
}

export const clientService = new ClientService();

